/**
 * @typedef {Object} BlockRecipesBuilder
 *
 * @property {{default: RecipeConfig, nested: RecipeConfig}} recipes
 * @property {function} initRecipesFromComponents
 * @property {function} generateRecipe
 * @property {function} getDefaultBlockRecipe
 */

/**
 * @typedef {Object} RecipeConfig
 *
 * @property {string} componentName
 * @property {string[]} inputs
 * @property {string[]} classes
 * @property {string[]} parentClasses
 * @property {string[]} outputs
 * @property {string} wrapStart
 * @property {string} wrapEnd
 */

/**
 * @typedef {Object} Recipe
 *
 * @property {string} template
 * @property {RecipeConfig} config
 */

(function() {
    'use strict';

    angular.module( APP_ID )
        .factory( 'BlockRecipesBuilder', [ 'LoggerFactory', '$injector', BlockRecipesBuilderService ]);


    /**
     * Provides tool for building, maintaining and customisation of templates
     * dynamic blocks' templates.
     *
     * @param {LoggerFactory} LoggerFactory
     * @param {ng.auto.IInjectorService} $injector
     *
     * @returns {BlockRecipesBuilder}
     */
    function BlockRecipesBuilderService( LoggerFactory, $injector ){





        /* ===== PRIVATE MEMBERS ============================================ */


        /** @type {Logger} */
        var logger = LoggerFactory.createLogger( "BlockRecipesBuilderService", false );


        /** @type {enum} */
        var COMPONENT_ATTRIBUTES = {
            RECIPES_DEFAULT:   'renderingOptions',
            RECIPES_OVERRIDES: 'renderingOptionsOverrides'
        };

        /** @type {regexp} */
        var HYPHEN_CASE_REGEXP = /[A-Z]/g;

        /** @type {RecipeConfig} */
        var DEFAULT_CONFIG = {
            componentName: null,
            inputs: [
                'content="::content.asset_contents"'
            ]
        };

        /**
         * Component to be used when directive/component does not exist.
         *
         * @type {RecipeConfig}
         */
        var FALLBACK_COMPONENT_CONFIG = {
            componentName: 'content-blank',
            inputs: [
                'content="::content"',
                'type="::type"'
            ],
            parentClasses: [ 'blank' ]
        };


        /** @type {Object} */
        var recipes = {
            // Available recipe 'types'
            /** @type {Recipe} */
            default: {},

            /** @type {Recipe} */
            nested:  {}
        };


        /** @type BlockRecipesBuilder */
        var service = {
            // Members
            recipes:                   recipes,

            // Methods
            initRecipesFromComponents: initRecipesFromComponents,
            generateRecipe:            generateRecipe,
            getDefaultBlockRecipe:     getDefaultBlockRecipe
        };


        var isInitialised = false;



        ////////////////////////////////////////////////////////////////////////

        return service; // Expose API

        ////////////////////////////////////////////////////////////////////////





        /* ===== PUBLIC METHODS ============================================= */


        /**
         * Reads configuration {RecipeConfig} on given component and
         * produces {Recipe} out of that.
         */
        function initRecipesFromComponents() {
            if ( isInitialised ) return;

            var componentsList = getAllComponents(),

            componentName, componentConfig;

            for ( var i = 0; i < componentsList.length; i++ ){

                componentName   = componentsList[ i ][ 0 ];
                componentConfig = componentsList[ i ][ 1 ];

                // Construct all the defaults
                if ( componentConfig.hasOwnProperty( COMPONENT_ATTRIBUTES.RECIPES_DEFAULT ) ) {
                    initDefaultRecipes( hyphenCase( componentName ), componentConfig[ COMPONENT_ATTRIBUTES.RECIPES_DEFAULT ] );
                }

                if ( componentConfig.hasOwnProperty( COMPONENT_ATTRIBUTES.RECIPES_DEFAULT ) ) {
                    initOverridingRecipes( hyphenCase( componentName ), componentConfig[ COMPONENT_ATTRIBUTES.RECIPES_OVERRIDES ] );
                }
            }

            isInitialised = true;
        }


        /**
         * Generates and retrieves HTML recipe (~template) that can be added to
         * DOM and compiled.
         *
         * @param {string} contentType Name or ID of the dynamic content block.
         * @param {RecipeConfig} configObject
         * @param {string} extensionType What kind of recipe type (default|nested|...) should be recipe generated for.
         *
         * @returns {Recipe}
         */
        function generateRecipe( contentType, configObject, extensionType ){
            extensionType = extensionType || 'default';

            if ( recipes.hasOwnProperty( extensionType ) && recipes[ extensionType ].hasOwnProperty( contentType ) ){
                logger.error( "Recipe is already defined, cannot generate a new one." );

                return recipes[ extensionType ][ contentType ];
            }

            // Override the default
            if ( extensionType !== 'default' ){
                if ( !recipes.default.hasOwnProperty( contentType ) ){
                    logger.error( "Cannot override a recipe & create `%s` extension as the default one does not exist yet.", extensionType );
                    return;
                }

                var defaultConfig = recipes.default[ contentType ].config;

                for ( var attributeName in defaultConfig ){
                    if ( !configObject.hasOwnProperty( attributeName ) ){
                        // Doesn't exist on extension config -> Copy from default one
                        configObject[ attributeName ] = defaultConfig[ attributeName ];
                    }
                }
            }

            recipes[ extensionType ][ contentType ] = makeRecipe( configObject );

            // Make the recipe read-only!
            Object.freeze( recipes[ extensionType ][ contentType ] );

            return recipes[ extensionType ][ contentType ];
        }


        /**
         * Check if the default block has existing component or directive.
         * If yes, return recipe for the default one.
         * if not, use the fall-back one.
         *
         * @param {any} contentType
         * @returns {Recipe}
         */
        function getDefaultBlockRecipe( contentType ) {
            var configObject     = {},
            camelisedContentType = $.camelCase( contentType );

            if ( $injector.has( camelisedContentType + 'Directive' ) || $injector.has( camelisedContentType + 'Component' ) ) {

                configObject = {
                    componentName: contentType,
                    inputs:        DEFAULT_CONFIG.inputs
                };

            } else {
                configObject = FALLBACK_COMPONENT_CONFIG;
            }

            return makeRecipe ( configObject );
        }





        /* ===== PRIVATE METHODS ============================================ */


        /**
         * Transforms camel case String into hyphen-based String
         * (eg. camelCase into camel-case).
         *
         * @param {string} name
         *
         * @returns {string}
         */
        function hyphenCase( name ) {
          return name.replace( HYPHEN_CASE_REGEXP, function( letter, pos ) {
            return (pos ? '-' : '') + letter.toLowerCase();
          });
        }


        /**
         * Generates recipe and returns it along with its original configuration.
         *
         * @param {RecipeConfig} configObject
         *
         * @returns {Recipe}
         */
        function makeRecipe( configObject ) {
            return {
                template: constructComponentTemplate( configObject ),
                config:   configObject
            };
        }


        /**
         * Retrieves all components available on $angular.auto.injector()
         *
         * @returns {angular.IModule[]}
         */
        function getAllComponents() {
            var components = [],
            invokes = angular.module( APP_ID )._invokeQueue;

            for ( var i in invokes ) {
                if ( invokes[i][1] === 'component' ) components.push( invokes[i][2] );
            }

            return components;
        }


        /**
         * Generates the unique recipe (template) for all the default recipe types.
         * (see this.recipes keys[])
         *
         * @param {string} componentName Name or ID of the dynamic content block.
         * @param {RecipeConfig[]} configObjects
          */
        function initDefaultRecipes( componentName, configObjects ){
            for ( var contentType in configObjects ) {
                configObjects[ contentType ].componentName = componentName;
                generateRecipe( contentType, configObjects[ contentType ] );
            }
        }


        /**
         * If there's an overriding {RecipeConfig} Object defined on the component,
         * override the `default` one first and then generate unique recipe (template).
         *
         * @param {string} componentName Name or ID of the dynamic content block.
         * @param {RecipeConfig} extensionsConfigObject List of attributes on {RecipeConfig} that should be overriden on the default recipe types.
         */
        function initOverridingRecipes( componentName, extensionsConfigObject ) {
            for ( var extensionType in extensionsConfigObject ) {

                for ( var contentType in extensionsConfigObject[ extensionType ] ) {

                    var overridingConfigObject = extensionsConfigObject[ extensionType ][ contentType ];

                    overridingConfigObject.componentName = componentName;

                    generateRecipe( contentType, overridingConfigObject, extensionType );
                }
            }
        }


        /**
         * Creates a modified HTML recipe (template) for Angular's component
         * based on given configuration.
         *
         * @param {RecipeConfig} configObject
         *
         * @returns {string}
         */
        function constructComponentTemplate( configObject ) {

            // 0. Init template ~ empty it.
            var template = '', i = 0;

            // 1. Wrapper's starting HTML element
            if ( configObject.wrapStart ) template += configObject.wrapStart;

            // 2. Component's starting element
            template += '<' + configObject.componentName;


            // 3. All component's attributes

            // 3A) All component's inputs
            if ( configObject.inputs ) {
                for ( i = 0; i < configObject.inputs.length; i++ ) {
                    template += ' ' + configObject.inputs[ i ];
                }
            }

            // 3B) All component's outputs
            if ( configObject.outputs ) {
                for ( i = 0; i < configObject.outputs.length; i++ ) {
                    template += ' ' + configObject.outputs[ i ];
                }
            }

            // 3C) All component's classes
            if ( configObject.classes && configObject.classes.length > 0 ){
                template += ' class="';

                for ( i = 0; i < configObject.classes.length; i++ ){
                    template += configObject.classes[ i ] + ' ';
                }

                template += '"';
            }

            // 4. Component's ending element
            template += '></' + configObject.componentName + '>';

            // 5. Wrapper's ending HTML element
            if ( configObject.wrapEnd ) template += configObject.wrapEnd;

            return template;
        }


    }
})();
